import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import type {
  DeleteShortLinkInput,
  DeleteShortLinkResult,
  IssueShortLinkInput,
  IssueShortLinkResult,
  ListShortLinksInput,
  ListShortLinksResult,
  ShortLinkSummary,
  UpdateShortLinkInput,
  UpdateShortLinkResult,
} from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * 短縮アドレス (docs/26) の管理 callable hooks。
 *
 * functions/src/shortlink/manageShortLinks.ts の issueShortLink / listShortLinks /
 * updateShortLink / deleteShortLink を呼ぶ。`shortLinks` は firestore.rules で client 完全 deny
 * (短いコードの列挙を防ぐため) なので、購読ではなく callable + 手動 refresh 方式にしている
 * (`useMcpTokens` と同じ構造)。
 *
 * 認可は callable 側で強制 (master = 任意地区 / 地区管理者 = 自地区のみ)。
 */

export type ShortLinksListState =
  | { status: 'idle'; links: []; error: null }
  | { status: 'loading'; links: ShortLinkSummary[]; error: null }
  | { status: 'ready'; links: ShortLinkSummary[]; error: null }
  | { status: 'error'; links: []; error: Error };

export interface UseShortLinksReturn {
  state: ShortLinksListState;
  /** 一覧を (再) 取得。mutation 後に呼んで refresh する。 */
  refresh: (districtId: string) => Promise<void>;
  issueLink: (input: IssueShortLinkInput) => Promise<IssueShortLinkResult>;
  updateLink: (input: UpdateShortLinkInput) => Promise<UpdateShortLinkResult>;
  deleteLink: (input: DeleteShortLinkInput) => Promise<DeleteShortLinkResult>;
}

export function useShortLinks(): UseShortLinksReturn {
  const [state, setState] = useState<ShortLinksListState>({
    status: 'idle',
    links: [],
    error: null,
  });

  const refresh = useCallback(async (districtId: string) => {
    setState((prev) => ({
      status: 'loading',
      links: prev.status === 'ready' || prev.status === 'loading' ? prev.links : [],
      error: null,
    }));
    try {
      const fn = httpsCallable<ListShortLinksInput, ListShortLinksResult>(
        functions,
        'listShortLinks',
      );
      const res = await fn({ districtId });
      setState({ status: 'ready', links: res.data.links ?? [], error: null });
    } catch (e) {
      setState({ status: 'error', links: [], error: e as Error });
    }
  }, []);

  const issueLink = useCallback(async (input: IssueShortLinkInput) => {
    const fn = httpsCallable<IssueShortLinkInput, IssueShortLinkResult>(
      functions,
      'issueShortLink',
    );
    const res = await fn(input);
    return res.data;
  }, []);

  const updateLink = useCallback(async (input: UpdateShortLinkInput) => {
    const fn = httpsCallable<UpdateShortLinkInput, UpdateShortLinkResult>(
      functions,
      'updateShortLink',
    );
    const res = await fn(input);
    return res.data;
  }, []);

  const deleteLink = useCallback(async (input: DeleteShortLinkInput) => {
    const fn = httpsCallable<DeleteShortLinkInput, DeleteShortLinkResult>(
      functions,
      'deleteShortLink',
    );
    const res = await fn(input);
    return res.data;
  }, []);

  return { state, refresh, issueLink, updateLink, deleteLink };
}
